import { useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { useToast } from "@/components/ui/use-toast";
import {
  StagedTodo,
  TranscriptionView,
  useApproveTodo,
  useListCustomers,
  useListStagedTodos,
} from "@/lib/api/customer";
import { Trader } from "@/lib/api/reports";
import { useListTraders } from "@/lib/api/trader";
import { AllTranscriptionsList } from "./AllTranscriptionsList";
import { HideTodoDialog } from "./HideTodoDialog";
import { ManageTodoDialog } from "./ManageTodoDialog";
import { StagedTodoCard } from "./StagedTodoCard";
import { TodoTranscriptDialog } from "./TodoTranscript";
import { TranscriptionViewFilterButtons } from "./TranscriptionViewFilter";

const PAGE_SIZE = 25;
const emptyMessage: Record<TranscriptionView, string> = {
  needs_review: "No Todos need review.",
  visible: "No approved Todos.",
  removed: "No removed Todos.",
};
const API_DATE_FORMAT = "YYYY-MM-DD";

type TranscriptionsMode = "todos" | "all";

export function TranscriptionsDashboard() {
  const { toast } = useToast();
  const approveTodo = useApproveTodo();

  const [mode, setMode] = useState<TranscriptionsMode>("todos");
  const [page, setPage] = useState(1);
  const [allPage, setAllPage] = useState(1);
  const [view, setView] = useState<TranscriptionView>("needs_review");
  const [customerId, setCustomerId] = useState("");
  const [traderId, setTraderId] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const [todoToManage, setTodoToManage] = useState<StagedTodo | null>(null);
  const [todoToHide, setTodoToHide] = useState<StagedTodo | null>(null);
  const [transcriptTodo, setTranscriptTodo] = useState<StagedTodo | null>(null);
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [isAllLoading, setIsAllLoading] = useState(false);

  const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const tradersResult = useListTraders();
  const stagedQuery = useListStagedTodos({
    view,
    customer_id: customerId || undefined,
    trader_id: traderId || undefined,
    start_date: startDate ? moment(startDate).format(API_DATE_FORMAT) : undefined,
    end_date: endDate ? moment(endDate).format(API_DATE_FORMAT) : undefined,
    page,
    pageSize: PAGE_SIZE,
  }, mode === "todos");

  const customers: { id: string; long_name: string }[] = customersResult.data?.data || [];
  const customerOptions = customers
    .map((customer: { id: string; long_name: string }) => ({ value: customer.id, label: customer.long_name }))
    .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
  const traders: Trader[] = tradersResult.data?.data || [];
  const traderOptions = traders
    .map((trader: Trader) => ({ value: trader.id, label: trader.name }))
    .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));

  const stagedResult = stagedQuery.data;
  const todos: StagedTodo[] = stagedResult?.success ? stagedResult.data.data : [];
  const pagination = stagedResult?.success ? stagedResult.data.pagination : null;
  const loadError = stagedQuery.isError
    ? "An unexpected error occurred while loading staged Todos."
    : stagedResult && !stagedResult.success
      ? stagedResult.message || "Staged Todos could not be loaded."
      : null;
  const isLoading = mode === "todos" ? stagedQuery.isFetching : isAllLoading;
  const hasFilters = !!(customerId || traderId || startDate || endDate);

  // Approving or hiding the last row on the final page shrinks the page count.
  if (pagination && pagination.totalPages > 0 && page > pagination.totalPages) {
    setPage(pagination.totalPages);
  }

  // Filters change the row count, so stay on a page that exists.
  const updateFilter = (apply: () => void) => {
    setPage(1);
    setAllPage(1);
    apply();
  };

  const clearFilters = () => {
    setPage(1);
    setAllPage(1);
    setCustomerId("");
    setTraderId("");
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const handleApprove = async (todo: StagedTodo) => {
    if (approvingIds.has(todo.id)) return;

    setApprovingIds((prev: Set<string>) => new Set(prev).add(todo.id));

    try {
      const result = await approveTodo({ todo_id: todo.id });

      if (!result.success) {
        toast({
          title: "Unable to approve Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Todo approved",
        description: "The Todo now appears in the To-Dos list.",
      });
    } catch {
      toast({
        title: "Unable to approve Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setApprovingIds((prev: Set<string>) => {
        const next = new Set(prev);
        next.delete(todo.id);
        return next;
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline gap-2">
          <h1 className="text-2xl font-bold">Transcriptions</h1>
          {isLoading && (
            <span className="text-xs text-muted-foreground" data-testid="transcriptions-loading">
              Loading...
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {mode === "todos"
            ? "Review Todos created from call recordings. Approved Todos appear in the To-Dos list; removed Todos are hidden from both lists."
            : "Every transcribed call, with its transcripts and any Todos created from it."}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div
          role="group"
          aria-label="Choose what to list"
          className="inline-flex h-9 items-center overflow-hidden rounded-md border border-input"
        >
          <Button
            type="button"
            size="sm"
            variant={mode === "todos" ? "default" : "ghost"}
            aria-pressed={mode === "todos"}
            data-testid="transcriptions-mode-todos"
            onClick={() => setMode("todos")}
            className="rounded-none focus-visible:ring-offset-0"
          >
            Automated ToDos
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "all" ? "default" : "ghost"}
            aria-pressed={mode === "all"}
            data-testid="transcriptions-mode-all"
            onClick={() => setMode("all")}
            className="rounded-none border-l border-input focus-visible:ring-offset-0"
          >
            All Transcriptions
          </Button>
        </div>
        {mode === "todos" && (
          <TranscriptionViewFilterButtons
            className="h-9"
            value={view}
            onValueChange={(value: TranscriptionView) => updateFilter(() => setView(value))}
          />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Customer</span>
          <Combobox
            id="transcriptionsCustomerFilter"
            dwimOnChange
            virtualizeList
            options={customerOptions}
            value={customerId}
            onValueChange={(value: string) => updateFilter(() => setCustomerId(value))}
            placeholder={customersResult.isLoading ? "Loading customers..." : "Any customer"}
            className="w-[260px]"
            popoverClassName="min-w-[320px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Trader</span>
          <Combobox
            id="transcriptionsTraderFilter"
            dwimOnChange
            virtualizeList
            popoverClassName="md:min-w-[300px] lg:min-w-[400px]"
            options={traderOptions}
            value={traderId}
            onValueChange={(value: string) => updateFilter(() => setTraderId(value))}
            placeholder={tradersResult.isLoading ? "Loading traders..." : "Any trader"}
            className="w-[200px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Start Date</span>
          <DatePicker
            id="transcriptionsStartDate"
            containerClassName="w-[170px]"
            value={startDate}
            onChange={(date: Date | undefined) => updateFilter(() => setStartDate(date))}
            maxDate={endDate}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">End Date</span>
          <DatePicker
            id="transcriptionsEndDate"
            containerClassName="w-[170px]"
            value={endDate}
            onChange={(date: Date | undefined) => updateFilter(() => setEndDate(date))}
            minDate={startDate}
          />
        </div>
        {hasFilters && (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {mode === "all" && (
        <AllTranscriptionsList
          customerId={customerId}
          traderId={traderId}
          startDate={startDate}
          endDate={endDate}
          hasFilters={hasFilters}
          onLoadingChange={setIsAllLoading}
          page={allPage}
          onPageChange={setAllPage}
        />
      )}

      {mode === "todos" && (
        <>
          {!stagedQuery.isLoading && loadError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
              <p>{loadError}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => stagedQuery.refetch()}
                disabled={stagedQuery.isFetching}
              >
                {stagedQuery.isFetching ? "Retrying..." : "Try Again"}
              </Button>
            </div>
          )}

          {!stagedQuery.isLoading && !loadError && todos.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {hasFilters ? "No Todos match these filters." : emptyMessage[view]}
            </div>
          )}

          {!stagedQuery.isLoading && !loadError && todos.length > 0 && (
            <div className="space-y-2">
              {todos.map((todo: StagedTodo) => (
                <StagedTodoCard
                  key={todo.id}
                  todo={todo}
                  isApproving={approvingIds.has(todo.id)}
                  onViewTranscript={() => setTranscriptTodo(todo)}
                  onManage={() => setTodoToManage(todo)}
                  onHide={() => setTodoToHide(todo)}
                  onApprove={() => handleApprove(todo)}
                />
              ))}
            </div>
          )}

          {pagination && pagination.totalPages > 0 && (
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(page - 1, 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems.toLocaleString()} Todos)
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(page + 1, pagination.totalPages))}
                disabled={page >= pagination.totalPages}
              >
                Next
              </Button>
            </div>
          )}

          <ManageTodoDialog
            todo={todoToManage}
            onClose={() => setTodoToManage(null)}
            showReviewActions
          />
          <HideTodoDialog todo={todoToHide} onClose={() => setTodoToHide(null)} />
          <TodoTranscriptDialog
            todo={transcriptTodo}
            onClose={() => setTranscriptTodo(null)}
          />
        </>
      )}
    </div>
  );
}
